import { ReactElement, useMemo, useCallback } from 'react';
import { MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus as AddManualIcon,
  faFile as ImportFromFileIcon,
  faPlug as ImportFromIntegrationIcon,
  faWarehouse as ImportFromAssociatedCompany,
} from '@fortawesome/free-solid-svg-icons';

import { PlanFeaturesTypes } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useSubscriptionFeatures } from '~hooks/useSubscriptionFeatures';
import { selectAuth } from '~store/slices/auth';
import { isOnlyCreatorOrder } from '~utils/user';
import { getFeatureAvailableToDate } from '~utils/feature';

import { AddOrdersActionsMenuProps } from './types';
import { AddOrdersActionsMenuContainer } from './styles';

const AddOrdersActionsMenu = ({
  anchorEl,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'center',
  },
  transformOrigin = {
    vertical: 'top',
    horizontal: 'center',
  },
  open,
  onClose,
  items,
}: AddOrdersActionsMenuProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const { isCarrier } = useTypePartnerCompany();
  const { hasAccessToFeature } = useSubscriptionFeatures();

  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const hasAccessMgmGiversModules = useMemo(
    () =>
      hasAccessToFeature(
        PlanFeaturesTypes.MgmGiversModule,
        getFeatureAvailableToDate(PlanFeaturesTypes.MgmGiversModule),
      ),
    [hasAccessToFeature],
  );

  const isEnabledManualItem = useMemo(
    () => !items.manual.disabled,
    [items.manual.disabled],
  );

  const isEnabledFromFileItem = useMemo(
    () => !items.fromFile.disabled,
    [items.fromFile.disabled],
  );

  const isEnabledFromIntegrationItem = useMemo(
    () => !items.fromIntegration.disabled && !isUserOnlyCreatorOrder,
    [items.fromIntegration.disabled, isUserOnlyCreatorOrder],
  );

  const isEnabledFromAssociatedCompanyItem = useMemo(() => {
    if (!hasAccessMgmGiversModules) return false;

    return (
      !items?.fromAssociatedCompany?.disabled &&
      isCarrier &&
      !isUserOnlyCreatorOrder
    );
  }, [
    hasAccessMgmGiversModules,
    items?.fromAssociatedCompany?.disabled,
    isCarrier,
    isUserOnlyCreatorOrder,
  ]);

  const handleClickItem = useCallback(
    (callback?: () => void) => {
      callback?.();
      onClose?.({}, 'backdropClick');
    },
    [onClose],
  );

  return (
    <AddOrdersActionsMenuContainer
      id="add-orders-actions-menu"
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      open={open}
      onClose={onClose}
      elevation={0}
    >
      {isEnabledManualItem && (
        <MenuItem onClick={() => handleClickItem(items.manual.onAction)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={AddManualIcon} />
          </ListItemIcon>

          <ListItemText primary="Manualmente" />
        </MenuItem>
      )}

      {isEnabledFromFileItem && (
        <MenuItem onClick={() => handleClickItem(items.fromFile.onAction)}>
          <ListItemIcon>
            <FontAwesomeIcon icon={ImportFromFileIcon} />
          </ListItemIcon>

          <ListItemText primary="Desde archivo" />
        </MenuItem>
      )}

      {isEnabledFromIntegrationItem && (
        <MenuItem
          onClick={() => handleClickItem(items.fromIntegration.onAction)}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={ImportFromIntegrationIcon} />
          </ListItemIcon>

          <ListItemText primary="Desde tiendas" />
        </MenuItem>
      )}

      {items.fromAssociatedCompany && isEnabledFromAssociatedCompanyItem && (
        <MenuItem
          onClick={() =>
            handleClickItem(items?.fromAssociatedCompany?.onAction)
          }
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={ImportFromAssociatedCompany} />
          </ListItemIcon>

          <ListItemText primary="Recepcionar pedidos" />
        </MenuItem>
      )}
    </AddOrdersActionsMenuContainer>
  );
};

export default AddOrdersActionsMenu;
